// src/components/admin/Views/Media/GenerateAudio.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    generateAudioForContents,
    getContentsDetails,
    ContentDetails,
    TtsResult,
    TtsSummary,
    recalcDurationsForContent,
    RecalcDurationsResponse,
} from '../../../../api/generateAudio';

import '../../../../assets/styles/generate-audio.scss';

type UiLang = {
    code: string;
    label: string;
    checked: boolean;
};

const LANGS: UiLang[] = [
    { code: 'PL', label: 'Polski', checked: true },
    { code: 'DE', label: 'Niemiecki', checked: true },
    { code: 'EN', label: 'Angielski', checked: true },
    { code: 'ES', label: 'Hiszpański', checked: true },
    { code: 'FR', label: 'Francuski', checked: true },
    { code: 'IT', label: 'Włoski', checked: true },
    { code: 'UK', label: 'Ukraiński', checked: true },
];

const MAX_BATCH = 50;

function fmtDate(dateStr?: string | null) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString();
}

function statusLabel(status?: string) {
    switch (status) {
        case 'created':
            return 'Utworzono audio';
        case 'skipped_existing':
            return 'Pominięto – audio już istnieje';
        case 'skipped_no_text':
            return 'Pominięto – brak tekstu';
        case 'skipped_too_long':
            return 'Pominięto – tekst za długi';
        case 'error':
            return 'Błąd podczas generowania';
        default:
            return status || '—';
    }
}

export default function GenerateAudio() {
    const [contents, setContents] = useState<ContentDetails[]>([]);
    const [contentsLoading, setContentsLoading] = useState(false);
    const [contentsError, setContentsError] = useState<string | null>(null);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const [langs, setLangs] = useState<UiLang[]>(LANGS);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generateError, setGenerateError] = useState<string | null>(null);
    const [summary, setSummary] = useState<TtsSummary | null>(null);
    const [results, setResults] = useState<TtsResult[]>([]);

    // recalc durations
    const [recalcLoadingId, setRecalcLoadingId] = useState<number | null>(null);
    const [recalcError, setRecalcError] = useState<string | null>(null);
    const [recalcInfo, setRecalcInfo] = useState<
        Record<number, { updatedCount: number; failedCount: number; failed?: RecalcDurationsResponse['failed'] }>
    >({});

    // ===== POBIERANIE LISTY TREŚCI =====
    useEffect(() => {
        const load = async () => {
            setContentsLoading(true);
            setContentsError(null);
            try {
                const data = await getContentsDetails();
                setContents([...data].sort((a, b) => a.id - b.id));
            } catch (e: any) {
                console.error(e);
                setContentsError(
                    e?.response?.data?.error || e?.message || 'Nie udało się pobrać listy treści'
                );
            } finally {
                setContentsLoading(false);
            }
        };
        load();
    }, []);

    // ===== SELEKCJA TREŚCI =====

    const toggleSelectOne = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === contents.length) {
            setSelectedIds([]);
        } else {
            const ids = contents.map(c => c.id).slice(0, MAX_BATCH);
            setSelectedIds(ids);
        }
    };

    // ===== JĘZYKI =====

    const toggleLang = (code: string) => {
        setLangs(prev =>
            prev.map(l =>
                l.code === code ? { ...l, checked: !l.checked } : l
            )
        );
    };

    const selectAllLangs = () => {
        setLangs(prev => prev.map(l => ({ ...l, checked: true })));
    };

    const clearLangs = () => {
        setLangs(prev => prev.map(l => ({ ...l, checked: false })));
    };

    const selectedLangCodes = useMemo(
        () => langs.filter(l => l.checked).map(l => l.code),
        [langs]
    );

    // ===== GENEROWANIE =====

    const runGenerate = async (contentIds: number[]) => {
        const filteredIds = contentIds.filter(n => Number.isFinite(n));
        if (!filteredIds.length) {
            setGenerateError('Nie wybrano żadnej treści');
            return;
        }
        if (!selectedLangCodes.length) {
            setGenerateError('Wybierz przynajmniej jeden język');
            return;
        }
        if (filteredIds.length > MAX_BATCH) {
            setGenerateError(`Możesz generować maksymalnie ${MAX_BATCH} treści jednocześnie`);
            return;
        }

        setIsGenerating(true);
        setGenerateError(null);
        setSummary(null);
        setResults([]);

        try {
            let total = 0;
            let created = 0;
            let skipped = 0;
            let errors = 0;
            const allResults: TtsResult[] = [];

            // GENERUJEMY SEKWENCYJNIE PO JEDNYM CONTENT_ID,
            // żeby pojedynczy HTTP request nie był gigantyczny
            for (const id of filteredIds) {
                const resp = await generateAudioForContents({
                    contentIds: [id],           // <── tylko jeden id na raz
                    languages: selectedLangCodes,
                    force: false,
                });

                total += resp.summary.total;
                created += resp.summary.created;
                skipped += resp.summary.skipped;
                errors += resp.summary.errors;
                allResults.push(...(resp.results || []));
            }

            setSummary({ total, created, skipped, errors });
            setResults(allResults);
        } catch (e: any) {
            console.error(e);
            setGenerateError(
                e?.response?.data?.error || e?.message || 'Błąd podczas generowania audio'
            );
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerateSingle = (id: number) => {
        runGenerate([id]);
    };

    const handleGenerateSelected = () => {
        runGenerate(selectedIds);
    };

    // ===== RECALC DURATION =====

    const handleRecalcDurations = async (contentId: number) => {
        setRecalcError(null);
        setRecalcLoadingId(contentId);
        try {
            const resp: RecalcDurationsResponse = await recalcDurationsForContent(contentId);
            setRecalcInfo(prev => ({
                ...prev,
                [contentId]: {
                    updatedCount: resp.updatedCount,
                    failedCount: resp.failedCount,
                    failed: resp.failed,
                },
            }));
        } catch (e: any) {
            console.error(e);
            setRecalcError(
                e?.response?.data?.error ||
                e?.message ||
                'Błąd podczas przeliczania długości audio'
            );
        } finally {
            setRecalcLoadingId(null);
        }
    };

    // ===== WYNIKI POGRUPOWANE PO TREŚCI =====

    const resultsByContent = useMemo(() => {
        const map = new Map<number, TtsResult[]>();
        for (const r of results) {
            const key = r.contentId;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(r);
        }
        return map;
    }, [results]);

    const getContentTitle = (id: number) => {
        const found = contents.find(c => c.id === id);
        return found ? found.title : `Tekst #${id}`;
    };

    const selectedCount = selectedIds.length;
    const isOverBatchLimit = selectedCount > MAX_BATCH;

    // ===== RENDER =====

    return (
        <div className="audio-generator-container">
            {/* Header */}
            <div className="audio-header-bar">
                <div className="audio-header-main">
                    <h2>Generowanie audio (Google TTS)</h2>
                    <p className="audio-subtitle">
                        Wybierz treści oraz języki. Audio wygeneruje się tylko tam, gdzie jeszcze go
                        nie ma. Maksymalnie {MAX_BATCH} treści na raz.
                    </p>
                </div>

                <div className="audio-header-actions">
                    <div className="audio-lang-section">
                        <span className="audio-label">Języki audio</span>
                        <div className="audio-lang-list">
                            {langs.map(l => (
                                <label
                                    key={l.code}
                                    className="filter-checkbox audio-lang-chip"
                                >
                                    <input
                                        type="checkbox"
                                        checked={l.checked}
                                        onChange={() => toggleLang(l.code)}
                                    />
                                    {l.label} ({l.code})
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="audio-lang-actions">
                        <button
                            type="button"
                            className="btn small"
                            onClick={selectAllLangs}
                        >
                            Zaznacz wszystkie języki
                        </button>
                        <button
                            type="button"
                            className="btn small ghost"
                            onClick={clearLangs}
                        >
                            Wyczyść języki
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista treści */}
            <div className="audio-body">
                {contentsLoading && (
                    <div className="audio-message audio-message-loading">
                        Ładowanie treści…
                    </div>
                )}

                {contentsError && (
                    <div className="audio-message audio-message-error">
                        <strong>Błąd:</strong> {contentsError}
                    </div>
                )}

                {!contentsLoading && !contentsError && (
                    <>
                        <div className="audio-toolbar">
                            <div className="audio-toolbar-left">
                                <span>Wybrane treści: <strong>{selectedCount}</strong></span>
                                {isOverBatchLimit && (
                                    <span className="audio-warning">
                                        Wybrano więcej niż {MAX_BATCH} treści – zawęź wybór.
                                    </span>
                                )}
                            </div>
                            <div className="audio-toolbar-right">
                                <button
                                    type="button"
                                    className="btn primary"
                                    onClick={handleGenerateSelected}
                                    disabled={
                                        isGenerating ||
                                        !selectedCount ||
                                        isOverBatchLimit
                                    }
                                >
                                    Generuj audio dla zaznaczonych
                                </button>
                            </div>
                        </div>

                        <div className="audio-table-wrap">
                            <table className="audio-table">
                                <thead>
                                    <tr>
                                        <th className="col-select">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    contents.length > 0 &&
                                                    selectedIds.length === contents.length
                                                }
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th className="col-id">ID</th>
                                        <th className="col-title">Tytuł</th>
                                        <th className="col-lang">Język</th>
                                        <th className="col-type">Typ</th>
                                        <th className="col-visible">Widoczne</th>
                                        <th className="col-premium">Premium</th>
                                        <th className="col-date">Data utworzenia</th>
                                        <th className="col-actions">Akcje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contents.map(c => {
                                        const isSelected = selectedIds.includes(c.id);
                                        const rowResults = resultsByContent.get(c.id) || [];
                                        const recalc = recalcInfo[c.id];
                                        const isRecalcLoading = recalcLoadingId === c.id;

                                        return (
                                            <tr key={c.id}>
                                                <td className="col-select-val">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectOne(c.id)}
                                                    />
                                                </td>
                                                <td className="col-id-val">{c.id}</td>
                                                <td className="col-title-val">
                                                    <div
                                                        className="cell-scroll"
                                                        title={c.title || '—'}
                                                    >
                                                        {c.title || '—'}
                                                    </div>
                                                </td>
                                                <td className="col-lang-val">{c.language}</td>
                                                <td className="col-type-val">{c.type}</td>
                                                <td className="col-visible-val strong">
                                                    <span
                                                        className={`badge ${c.is_visible ? 'badge-success' : 'badge-muted'
                                                            }`}
                                                    >
                                                        {c.is_visible ? 'TAK' : 'NIE'}
                                                    </span>
                                                </td>
                                                <td className="col-premium-val strong">
                                                    <span
                                                        className={`badge ${c.is_premium ? 'badge-success' : 'badge-muted'
                                                            }`}
                                                    >
                                                        {c.is_premium ? 'TAK' : 'NIE'}
                                                    </span>
                                                </td>
                                                <td className="col-date-val">
                                                    {fmtDate(c.created_at)}
                                                </td>
                                                <td className="col-actions-val">
                                                    <div className="audio-row-actions">
                                                        <button
                                                            type="button"
                                                            className="btn small"
                                                            onClick={() => handleGenerateSingle(c.id)}
                                                            disabled={isGenerating}
                                                        >
                                                            Generuj audio
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn small ghost"
                                                            onClick={() => handleRecalcDurations(c.id)}
                                                            disabled={isRecalcLoading}
                                                        >
                                                            {isRecalcLoading
                                                                ? 'Przeliczanie…'
                                                                : 'Przelicz długości audio'}
                                                        </button>
                                                    </div>

                                                    {rowResults.length > 0 && (
                                                        <div className="audio-row-results">
                                                            {rowResults.map((r, idx) => (
                                                                <div
                                                                    key={`${r.contentId}-${r.lang}-${idx}`}
                                                                >
                                                                    <strong>{r.lang}</strong>: {statusLabel(r.status)}
                                                                    {r.reason ? ` – ${r.reason}` : ''}
                                                                    {typeof r.durationSeconds === 'number' &&
                                                                        r.status === 'created' && (
                                                                            <> – {r.durationSeconds}s</>
                                                                        )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {recalc && (
                                                        <div className="audio-row-recalc">
                                                            Zaktualizowano długości audio: {recalc.updatedCount}
                                                            {recalc.failedCount
                                                                ? `, błędne pliki: ${recalc.failedCount}`
                                                                : ''}
                                                            {recalc.failed && recalc.failed.length > 0 && (
                                                                <ul>
                                                                    {recalc.failed.map(f => (
                                                                        <li key={f.id}>
                                                                            #{f.id} – {f.file_path} – {f.reason}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {contents.length === 0 && (
                                        <tr>
                                            <td colSpan={9}>
                                                Brak treści do wyświetlenia.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Panel wyników globalnych */}
            <div className="audio-footer">
                {generateError && (
                    <div className="audio-message audio-message-error">
                        <strong>Błąd generowania:</strong> {generateError}
                    </div>
                )}

                {recalcError && (
                    <div className="audio-message audio-message-error">
                        <strong>Błąd przeliczania długości:</strong> {recalcError}
                    </div>
                )}

                {isGenerating && (
                    <div className="audio-message audio-message-loading">
                        Generowanie audio…
                    </div>
                )}

                {summary && (
                    <div className="audio-summary">
                        <span>
                            Łącznie: <strong>{summary.total}</strong>
                        </span>
                        <span>
                            Utworzone: <strong>{summary.created}</strong>
                        </span>
                        <span>
                            Pominięte: <strong>{summary.skipped}</strong>
                        </span>
                        <span>
                            Błędy: <strong>{summary.errors}</strong>
                        </span>
                    </div>
                )}

                {results.length > 0 && (
                    <details className="audio-details">
                        <summary>Szczegóły ({results.length})</summary>
                        <ul className="audio-results-list">
                            {results.map((r, idx) => (
                                <li key={idx}>
                                    [{r.status}] Tekst #{r.contentId} – {getContentTitle(r.contentId)} – {r.lang}
                                    {r.reason ? ` – ${r.reason}` : ''}
                                    {typeof r.durationSeconds === 'number' &&
                                        r.status === 'created' &&
                                        ` – ${r.durationSeconds}s`}
                                </li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </div>
    );
}
